import type { LightweightChartAction } from './actions';
import type { LightweightChartState } from './types';
import type { OHLCVBar } from 'src/services/LightweightChartBarDataService/bars';

/**
 * Sort ascending by `time` and collapse duplicate timestamps (keeping the
 * later occurrence) into a single bar. Returned array is a new reference; the
 * input is left untouched.
 */
export function sortAndDedupBars(bars: readonly OHLCVBar[]): OHLCVBar[] {
    if (bars.length === 0) return [];
    const sorted = [...bars].sort((a, b) => a.time - b.time);
    const result: OHLCVBar[] = [];
    for (const bar of sorted) {
        const last = result[result.length - 1];
        if (last && last.time === bar.time) {
            result[result.length - 1] = bar;
        } else {
            result.push(bar);
        }
    }
    return result;
}

/**
 * Merge a realtime bar into a sorted-unique bar array. Returns the original
 * reference when the bar is out-of-order (older than the last retained bar),
 * so reducer no-op guards work via `Object.is`.
 */
export function applyRealtimeBar(bars: readonly OHLCVBar[], incoming: OHLCVBar, maxBars: number): readonly OHLCVBar[] {
    if (bars.length === 0) {
        return [{ ...incoming }];
    }
    const last = bars[bars.length - 1];
    if (incoming.time === last.time) {
        // Identical timestamps may carry materially-different OHLCV values; treat
        // any non-trivial change as state-progressing so no-op guards still trip
        // when nothing actually moved.
        if (
            last.open === incoming.open &&
            last.high === incoming.high &&
            last.low === incoming.low &&
            last.close === incoming.close &&
            last.volume === incoming.volume
        ) {
            return bars;
        }
        const next = bars.slice();
        next[next.length - 1] = { ...incoming };
        return next;
    }
    if (incoming.time > last.time) {
        const next = bars.slice();
        next.push({ ...incoming });
        if (next.length > maxBars) {
            next.splice(0, next.length - maxBars);
        }
        return next;
    }
    return bars;
}

export function lightweightChartReducer(
    state: LightweightChartState,
    action: LightweightChartAction,
): LightweightChartState {
    switch (action.type) {
        case 'SET_SYMBOL': {
            if (state.symbol === action.symbol) return state;
            return {
                ...state,
                symbol: action.symbol,
                bars: [],
                historyLoaded: false,
                error: null,
            };
        }

        case 'SET_BAR_LENGTH': {
            if (
                state.barLength.value === action.barLength.value &&
                state.barLength.period === action.barLength.period
            ) {
                return state;
            }
            return {
                ...state,
                barLength: action.barLength,
                bars: [],
                historyLoaded: false,
                error: null,
            };
        }

        case 'SET_SESSION': {
            if (state.session === action.session) return state;
            return {
                ...state,
                session: action.session,
                bars: [],
                historyLoaded: false,
                error: null,
            };
        }

        case 'SET_MODE': {
            if (state.mode === action.mode) return state;
            return { ...state, mode: action.mode };
        }

        case 'SET_HISTORY_LOOKBACK_DAYS': {
            // Reject NaN / Infinity / <= 0 at the boundary: NaN would silently
            // pass the `===` no-op check (`NaN !== NaN`) and trigger a fresh
            // reload on every dispatch. Truncate to an integer so callers can
            // safely pass fractional values (e.g. `90 * someFactor`) without
            // splintering the dedup guard across near-equal floats. Re-check
            // `nextDays <= 0` after truncation so sub-1 fractional inputs
            // (e.g. 0.5 → 0) don't slip through and persist a zero-day window.
            if (!Number.isFinite(action.days) || action.days <= 0) return state;
            const nextDays = Math.trunc(action.days);
            if (nextDays <= 0) return state;
            if (state.historyLookbackDays === nextDays) return state;
            return {
                ...state,
                historyLookbackDays: nextDays,
                bars: [],
                historyLoaded: false,
                error: null,
            };
        }

        case 'HISTORY_LOAD_START': {
            if (state.loading && !state.historyLoaded && state.error === null) {
                return state;
            }
            return {
                ...state,
                loading: true,
                historyLoaded: false,
                error: null,
            };
        }

        case 'HISTORY_LOAD_SUCCESS': {
            const sortedUnique = sortAndDedupBars(action.bars);
            return {
                ...state,
                bars: sortedUnique,
                loading: false,
                historyLoaded: true,
                error: null,
            };
        }

        case 'HISTORY_LOAD_ERROR': {
            if (!state.loading && state.error === action.error) return state;
            return {
                ...state,
                loading: false,
                error: action.error,
            };
        }

        case 'REALTIME_BAR': {
            const nextBars = applyRealtimeBar(state.bars, action.bar, state.maxBars);
            if (nextBars === state.bars) return state;
            return { ...state, bars: nextBars };
        }

        default: {
            const _exhaustive: never = action;
            return state;
        }
    }
}
